import React from 'react';
import { Shield, Lock, Eye, UserCheck, Mail, Phone, MapPin, Cookie, FileText, AlertCircle } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-black text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Your privacy matters to us. Learn how we collect, use, and protect your personal information.
          </p>
        </div>
      </div>

      {/* Quick Overview Cards */}
      <div className="max-w-6xl mx-auto px-6 -mt-8">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-r-4 border-black">
            <Lock className="w-10 h-10 text-black mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Secure Data</h3>
            <p className="text-gray-600">Your information is protected with industry-standard security measures</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-r-4 border-black">
            <Eye className="w-10 h-10 text-black mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Transparency</h3>
            <p className="text-gray-600">Clear information about how we collect and use your data</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-r-4 border-black">
            <UserCheck className="w-10 h-10 text-black mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Your Control</h3>
            <p className="text-gray-600">You have full control over your personal information</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          
          {/* Policy Header */}
          <div className="bg-black text-white p-8">
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">Our Commitment to Privacy</h2>
                <p className="text-gray-300 mt-1">Last updated: November 2025</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-10">
            
            {/* Introduction */}
            <div>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                This privacy policy sets out how <strong>VESHH-EON</strong> uses and protects any information that you provide when you visit our website and/or agree to purchase from us.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                VESHH-EON is committed to ensuring that your privacy is protected. Should we ask you to provide certain information by which you can be identified when using this website, you can be assured that it will only be used in accordance with this privacy statement.
              </p>
            </div>

            {/* Policy Updates */}
            <div className="bg-amber-50 border border-black rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-3">
                <AlertCircle className="w-6 h-6 text-black" />
                <h3 className="text-xl font-bold text-gray-900">Policy Updates</h3>
              </div>
              <p className="text-gray-700">
                VESHH-EON may change this policy from time to time by updating this page. You should check this page periodically to ensure that you are aware of any changes and adhere to them.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 text-black" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Information We Collect</h3>
              </div>
              
              <div className="pl-11 space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  We may collect the following information:
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Mail className="w-5 h-5 text-black mt-1" />
                    <div>
                      <h4 className="font-bold text-gray-900">Email Address</h4>
                      <p className="text-gray-700">Used to send product launches, shipping updates, and order details</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Phone className="w-5 h-5 text-black mt-1" />
                    <div>
                      <h4 className="font-bold text-gray-900">Mobile Number</h4>
                      <p className="text-gray-700">Used for communication, customer support, and order-related updates</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <UserCheck className="w-5 h-5 text-black mt-1" />
                    <div>
                      <h4 className="font-bold text-gray-900">Name</h4>
                      <p className="text-gray-700">Used for personalization and order processing</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* How We Use Information */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <Eye className="w-5 h-5 text-black" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">How We Use Your Information</h3>
              </div>
              
              <div className="pl-11">
                <p className="text-gray-700 leading-relaxed mb-4">
                  We require this information to understand your needs and provide you with a better service, particularly for the following reasons:
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <span className="text-black mt-1">•</span>
                    <p className="text-gray-700">Internal record keeping</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-black mt-1">•</span>
                    <p className="text-gray-700">To improve our products and services</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-black mt-1">•</span>
                    <p className="text-gray-700">To send promotional emails about new products, special offers, or other information which we think you may find interesting</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-black mt-1">•</span>
                    <p className="text-gray-700">To contact you for market research purposes via email, phone, or mail</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-black mt-1">•</span>
                    <p className="text-gray-700">To customize the website according to your interests</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Security */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <Lock className="w-5 h-5 text-black" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Security</h3>
              </div>
              
              <div className="pl-11">
                <div className="bg-green-50 border border-black rounded-lg p-6">
                  <p className="text-gray-900 leading-relaxed">
                    We are committed to ensuring that your information is secure. In order to prevent unauthorized access or disclosure, we have put in place suitable physical, electronic, and managerial procedures to safeguard and secure the information we collect online.
                  </p>
                </div>
              </div>
            </div>

            {/* Cookies Policy */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <Cookie className="w-5 h-5 text-black" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">How We Use Cookies</h3>
              </div>
              
              <div className="pl-11 space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  A cookie is a small file which asks permission to be placed on your computer's hard drive. Once you agree, the file is added and the cookie helps analyze web traffic or lets you know when you visit a particular site.
                </p>
                
                <div className="bg-blue-50 border border-black rounded-lg p-6 space-y-3">
                  <h4 className="font-bold text-gray-900">What Cookies Do:</h4>
                  <div className="space-y-2">
                    <p className="flex items-start space-x-2 text-gray-700">
                      <span>•</span>
                      <span>Allow web applications to respond to you as an individual</span>
                    </p>
                    <p className="flex items-start space-x-2 text-gray-700">
                      <span>•</span>
                      <span>Tailor operations to your needs, likes, and dislikes</span>
                    </p>
                    <p className="flex items-start space-x-2 text-gray-700">
                      <span>•</span>
                      <span>Help us analyze webpage traffic and improve our website</span>
                    </p>
                  </div>
                </div>
                
                <p className="text-gray-700 leading-relaxed">
                  We use traffic log cookies to identify which pages are being used. This helps us analyze data about webpage traffic and improve our website to tailor it to customer needs. We only use this information for statistical analysis purposes, and then the data is removed from the system.
                </p>
                
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Your Choice:</strong> A cookie in no way gives us access to your computer or any information about you, other than the data you choose to share with us. You can choose to accept or decline cookies. Most web browsers automatically accept cookies, but you can usually modify your browser settings to decline cookies if you prefer. Note that this may prevent you from taking full advantage of the website.
                  </p>
                </div>
              </div>
            </div>

            {/* Controlling Personal Information */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-black" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Controlling Your Personal Information</h3>
              </div>
              
              <div className="pl-11 space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  You may choose to restrict the collection or use of your personal information in the following ways:
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <span className="text-black mt-1">•</span>
                    <p className="text-gray-700">Whenever you are asked to fill in a form on the website, look for the box that you can click to indicate that you do not want the information to be used for direct marketing purposes</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-black mt-1">•</span>
                    <p className="text-gray-700">If you have previously agreed to us using your personal information for direct marketing purposes, you may change your mind at any time by contacting us</p>
                  </div>
                </div>
                
                <div className="bg-red-50 border border-black rounded-lg p-6 mt-6">
                  <h4 className="font-bold text-gray-900 mb-3">Your Data Rights:</h4>
                  <div className="space-y-2">
                    <p className="text-gray-700">
                      We will <strong>not sell, distribute, or lease</strong> your personal information to third parties unless we have your permission or are required by law to do so.
                    </p>
                    <p className="text-gray-700">
                      We may use your personal information to send you promotional information about third parties which we think you may find interesting, only if you tell us that you wish this to happen.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Corrections */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-black" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Correcting Your Information</h3>
              </div>
              
              <div className="pl-11">
                <p className="text-gray-700 leading-relaxed">
                  If you believe that any information we are holding about you is incorrect or incomplete, please contact us as soon as possible. We will promptly correct any information found to be incorrect.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Contact Us About Your Privacy</h3>
            <p className="text-gray-600">Have questions or concerns about your personal information?</p>
          </div>
          
          <div className="space-y-6">
            <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-black" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900">Email</h4>
                <p className="text-gray-600">support@veshheon.in</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-black" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900">Phone</h4>
                <p className="text-gray-600">+91 9480743215</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-black" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900">Address</h4>
                <p className="text-gray-600">
                  Plot No. 5, House No. 1104,<br />
                  Vrandavan Colony, Mahantesh Nagar<br />
                  Belagavi, Karnataka 590016
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Important Reminder */}
        <div className="mt-12 bg-black text-white rounded-2xl p-8">
          <div className="flex items-center space-x-3 mb-6">
            <Shield className="w-8 h-8 text-white" />
            <h3 className="text-2xl font-bold">Your Privacy Matters</h3>
          </div>
          
          <div className="space-y-3">
            <p className="flex items-start space-x-2">
              <span className="text-white">•</span>
              <span>We take your privacy seriously and have implemented appropriate measures to protect your information</span>
            </p>
            <p className="flex items-start space-x-2">
              <span className="text-white">•</span>
              <span>You have the right to access, correct, or request deletion of your personal data</span>
            </p>
            <p className="flex items-start space-x-2">
              <span className="text-white">•</span>
              <span>We will never sell your personal information to third parties without your explicit consent</span>
            </p>
            <p className="flex items-start space-x-2">
              <span className="text-white">•</span>
              <span>This policy is subject to change. Please review it periodically for updates</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
